// <Chem>: renders chemistry markup (§9.7): `CuSO4.5H2O`, `SO4^2-`, `Ca(OH)2(aq)`, `->`, `<=>`.
// Subscripts and superscripts are real <sub>/<sup>; arrows are inline SVG (fonts often lack ⇌).
import type { JSX } from 'preact';

type Part =
  | { t: 'text'; v: string }
  | { t: 'sub'; v: string }
  | { t: 'sup'; v: string }
  | { t: 'arrow'; v: 'forward' | 'equilibrium' }
  | { t: 'state'; v: string };

const SUBS: Record<string, string> = { '₀': '0', '₁': '1', '₂': '2', '₃': '3', '₄': '4', '₅': '5', '₆': '6', '₇': '7', '₈': '8', '₉': '9' };

/** Tokenise chemistry markup into display parts. Exported for tests and aria text. */
export function parseChem(markup: string): Part[] {
  const s = markup.replace(/[₀-₉]/g, (c) => SUBS[c] ?? c).replace(/[·•∙⋅]/g, '.');
  const parts: Part[] = [];
  const push = (p: Part) => {
    const last = parts[parts.length - 1];
    if (last && last.t === p.t && (p.t === 'text' || p.t === 'sub')) (last as { v: string }).v += p.v;
    else parts.push(p);
  };
  let i = 0;
  let prevAtom = false; // previous char was part of a formula (letter or ')')
  while (i < s.length) {
    const rest = s.slice(i);
    const arrow = /^(\s*)(<=>|<->|⇌|->|=>|→)(\s*)/.exec(rest);
    if (arrow) {
      push({ t: 'text', v: ' ' });
      parts.push({ t: 'arrow', v: arrow[2] === '<=>' || arrow[2] === '<->' || arrow[2] === '⇌' ? 'equilibrium' : 'forward' });
      push({ t: 'text', v: ' ' });
      i += arrow[0].length;
      prevAtom = false;
      continue;
    }
    const state = /^\((s|l|g|aq)\)/.exec(rest);
    if (state && prevAtom) {
      parts.push({ t: 'state', v: `(${state[1]})` });
      i += state[0].length;
      prevAtom = true;
      continue;
    }
    const ch = s[i]!;
    if (ch === '^') {
      const m = /^\^([0-9]*[+-]|[+-][0-9]*)/.exec(rest);
      if (m) {
        const raw = m[1]!;
        const sign = raw.includes('-') ? '−' : '+';
        const digits = raw.replace(/[+-]/g, '');
        parts.push({ t: 'sup', v: `${digits}${sign}` });
        i += m[0].length;
        prevAtom = false;
        continue;
      }
      // superscript digits only, e.g. isotope mass numbers: ^39K
      const d = /^\^([0-9]+)/.exec(rest);
      if (d) {
        parts.push({ t: 'sup', v: d[1]! });
        i += d[0].length;
        continue;
      }
    }
    if (/[0-9]/.test(ch)) {
      const m = /^[0-9]+/.exec(rest)![0];
      if (prevAtom) push({ t: 'sub', v: m });
      else push({ t: 'text', v: m });
      i += m.length;
      continue;
    }
    if (ch === '.' && prevAtom && /^\.\d*[A-Z(]/.test(rest)) {
      push({ t: 'text', v: '·' });
      i++;
      prevAtom = false;
      continue;
    }
    if (ch === '+' || ch === '-') {
      // trailing charge sign directly after a formula (e.g. Na+, Cl-) when not a separator " + "
      const next = s[i + 1];
      if (prevAtom && (next === undefined || next === ' ' || next === ',' || next === ')' || next === '}')) {
        parts.push({ t: 'sup', v: ch === '-' ? '−' : '+' });
        i++;
        prevAtom = false;
        continue;
      }
    }
    push({ t: 'text', v: ch });
    prevAtom = /[A-Za-z)\]]/.test(ch);
    i++;
  }
  return parts;
}

/** Speakable text for screen readers. */
export function chemSpoken(markup: string): string {
  return parseChem(markup)
    .map((p) => {
      switch (p.t) {
        case 'arrow':
          return p.v === 'forward' ? ' yields ' : ' is in equilibrium with ';
        case 'sup':
          return ` ${p.v.replace('−', ' minus').replace('+', ' plus')} `;
        case 'sub':
          return p.v;
        case 'state':
          return ` ${p.v.replace(/[()]/g, '')} `;
        default:
          return p.v.replace('·', ' dot ');
      }
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

function Arrow({ kind }: { kind: 'forward' | 'equilibrium' }) {
  return (
    <svg class="chem-arrow" viewBox="0 0 24 12" width="1.7em" height="0.85em" aria-hidden="true" focusable="false">
      {kind === 'forward' ? (
        <path d="M1 6h20M16 2l5 4-5 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
      ) : (
        <>
          <path d="M1 4h20l-5-3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M23 8H3l5 3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </>
      )}
    </svg>
  );
}

export function Chem({ children, class: cls }: { children: string; class?: string }): JSX.Element {
  const parts = parseChem(children);
  return (
    <span class={`chem${cls ? ` ${cls}` : ''}`}>
      <span aria-hidden="true">
        {parts.map((p, i) => {
          switch (p.t) {
            case 'sub':
              return <sub key={i}>{p.v}</sub>;
            case 'sup':
              return <sup key={i}>{p.v}</sup>;
            case 'arrow':
              return <Arrow key={i} kind={p.v} />;
            case 'state':
              return (
                <span key={i} class="chem-state">
                  {p.v}
                </span>
              );
            default:
              return p.v;
          }
        })}
      </span>
      <span class="visually-hidden">{chemSpoken(children)}</span>
    </span>
  );
}
